/* eslint-disable no-param-reassign */
/* eslint @typescript-eslint/no-explicit-any: 0 */
import {ComponentProps, ReactNode, useEffect, useMemo, useRef, useState} from 'react';

import {
    Box as MUIBox,
    Button,
    CircularProgress,
    List as MUIList,
    ListItem as MUIListItem,
    ListItemIcon,
    makeStyles,
    Paper as MUIPaper,
    styled,
    Tab,
    Tabs,
    Typography,
} from '@material-ui/core';
import BorderColorOutlinedIcon from '@material-ui/icons/BorderColorOutlined';
import {useHistory, useLocation} from 'react-router-dom';
import {FallbackProps, withErrorBoundary} from 'react-error-boundary';
import {Alert, AlertTitle} from '@material-ui/lab';
import {
    compose,
    spacing,
    palette,
    flexbox,
    shadows,
    display,
    sizing,
    positions,
    border,
    borders,
    color,
    styleFunctionSx,
} from '@material-ui/system';
import {format} from 'date-fns';
import RootStore from '../../Core/mobx/RootStore';
import AllStatus from '../Underwritings/AllStatus';
import CustomerPortalAPI, {
    Customer,
    Policy,
    PolicyRequirementStatus,
    PolicyRequirementType,
    PolicyStatus,
    PolicyType,
    Requirement,
} from '../../APIs/CustomerPortalAPI';
import {PolicyRequirementTypeOptions} from './SelectOptions';
import PolicyInformationForm from './PolicyInformationForm';
import PolicyDocumentForm from './PolicyDocumentForm';
// import {TabPanel} from '../../Core/UI';
import {usePoliciesForCustomerQuery} from './hooks/usePoliciesForCustomerQuery';
import {usePolicyMutation} from './hooks/usePolicyMutation';
import {useRequirementMutation} from './hooks/useRequirementMutation';

/* prettier-ignore */
const styleFunction = styleFunctionSx(compose(
    display,
    sizing,
    spacing,
    flexbox,
    shadows,
    palette,
    positions,
    color,
    border,
    borders
));

const Box = styled(MUIBox)(styleFunction);
const Flex = styled(Box)({display: 'flex'});
const List = styled(MUIList)(styleFunction);
const ListItem = styled(MUIListItem)(styleFunction);
const Paper = styled(MUIPaper)(styleFunction);

const ScrollContainer = styled(Box)({
    scrollBehavior: 'smooth',
    overflowY: 'auto',
    minHeight: '500px',
    height: '500px',
    flexGrow: 1,
});

type ScrollAwareContainerProps = ComponentProps<typeof ScrollContainer>;

function ScrollAwareContainer({children, ...props}: ScrollAwareContainerProps) {
    const ref = useRef<HTMLDivElement>(null);
    const [showShadow, setShowShadow] = useState(false);

    useEffect(() => {
        const onScroll = (e: Event) => {
            setShowShadow((e.target as HTMLDivElement)?.scrollTop !== 0);
        };

        ref.current?.addEventListener('scroll', onScroll);

        return () => {
            ref.current?.removeEventListener('scroll', onScroll);
        };
    }, []);

    return (
        <Flex sx={{flex: 1, position: 'relative', flexDirection: 'column'}}>
            <ScrollContainer {...{...props, ref}}>
                <Box
                    sx={{
                        height: '1px',
                        position: 'sticky',
                        top: '-1px',
                        right: 0,
                        left: 0,
                        boxShadow: '0 1px 10px rgba(0,0,0,0.9)',
                        borderBottom: '1px solid rgba(0,0,0,0.2)',
                    }}
                    style={{opacity: showShadow ? 1 : 0, transition: 'opacity 0.1s ease-in-out'}}
                />
                {children}
            </ScrollContainer>
        </Flex>
    );
}

function RightSidebarLayout({main, sidebar}: {main: ReactNode; sidebar: ReactNode}) {
    return (
        <Flex sx={{flex: '1'}}>
            <Flex sx={{flexBasis: '75%'}}>{main}</Flex>
            <Flex sx={{flexBasis: '25%', bgcolor: '#F8F8F8'}}>{sidebar}</Flex>
        </Flex>
    );
}

function FormPaper({children, sx, ...props}: ComponentProps<typeof Paper>) {
    return (
        <Paper
            sx={{
                p: '32px 24px 40px 41px',
                boxShadow: '0px 0px 6px rgba(0,0,0,0.13)',
                ...sx,
            }}
            {...props}
        >
            {children}
        </Paper>
    );
}

const useStyles = makeStyles({
    policyAddButtonText: {
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: '0.875rem',
        lineHeight: '24px',
        textAlign: 'center',
        textTransform: 'uppercase',
        color: '#ffffff',
    },
    requirementViewerLabel: {
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 700,
        fontSize: '1.25rem',
        lineHeight: '23px',
        color: '#000000',
    },
    requirementLabel: {
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 700,
        fontSize: '1rem',
        lineHeight: '23px',
        color: '#000000',
    },
    requirementAddButton: {
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: '0.875rem',
        lineHeight: '16px',
        textAlign: 'center',
        textTransform: 'uppercase',
        color: '#3B58B8',
    },
    requirements: {
        marginTop: '30px',
        overflowY: 'auto',
        // height: 'calc(100vh - 430px)',
    },
    requirementItem: {
        display: 'flex',
        padding: '20px',
        borderRadius: '4px',
        marginBottom: '16px',
    },
});

// Reacreating the TabPanel here so as to not disturb the other panel which
// makes assumptions about the parent container
type TabPanelProps = {
    index: number;
    value: number;
} & ComponentProps<typeof Box>;

function TabPanel({children, value, index, ...props}: TabPanelProps) {
    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`policy-tabpanel-${index}`}
            aria-labelledby={`policy-tab-${index}`}
            {...props}
        >
            {value === index && children}
        </Box>
    );
}

type PolicyRequirementStatusPanelProps = {
    policy?: Policy;
    selectedRequirement: Requirement | null;
    onAddRequirement: () => void;
    onSelectRequirement: (requirement: Requirement) => void;
};

type RequirementListItemProps = {
    active?: boolean;
    requirement: Requirement;
    onClick: (requirement: Requirement) => void;
};

function RequirementListItem({requirement, active, onClick}: RequirementListItemProps) {
    return (
        <ListItem
            sx={{
                bgcolor: active ? '#E1E5F1' : 'transparent',
                p: '20px',
                mb: '4px',
                borderRadius: '4px',
                alignItems: 'flex-start',
            }}
            button
            data-id={requirement.id}
            onClick={() => onClick(requirement)}
            key={requirement.id}
        >
            <ListItemIcon>
                {!requirement.id ? (
                    <CircularProgress size={18} />
                ) : (
                    <BorderColorOutlinedIcon color="primary" style={{paddingTop: '0.3em'}} />
                )}
            </ListItemIcon>
            <Flex sx={{flexDirection: 'column'}}>
                <Typography color="primary" variant="subtitle1">
                    {/* TODO: change this to a lookup map with default fallback */}
                    {PolicyRequirementTypeOptions.filter((req) => req.value === requirement.type)[0]?.label}
                </Typography>
                <Typography color="primary" variant="subtitle2">
                    {AllStatus.find((status) => status.name === requirement.status)?.description}
                </Typography>
            </Flex>
        </ListItem>
    );
}

function PolicyRequirementStatusPanel({
    policy,
    selectedRequirement,
    onSelectRequirement,
    onAddRequirement,
}: PolicyRequirementStatusPanelProps) {
    const classes = useStyles();

    return (
        <Flex sx={{flexDirection: 'column', flex: 1}}>
            <Flex
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pt: '26px',
                    pb: 1,
                    px: 3,
                }}
            >
                <Typography classes={{root: classes.requirementLabel}}>Requirements</Typography>
                {policy?.id && (
                    <Button type="button" onClick={onAddRequirement} color="primary">
                        + Add Requirement
                    </Button>
                )}
            </Flex>
            <ScrollAwareContainer sx={{px: 3, pt: 0}} component={List}>
                {policy?.requirements?.map((requirement) => (
                    <RequirementListItem
                        requirement={requirement}
                        onClick={onSelectRequirement}
                        active={selectedRequirement?.id === requirement.id}
                    />
                ))}
            </ScrollAwareContainer>
        </Flex>
    );
}

type PolicyRequirementProps = {
    index: number;
    value: Requirement;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function PolicyRequirement({index, value}: PolicyRequirementProps) {
    return value.type === PolicyRequirementType.POLICY_DOCUSIGN ? (
        <PolicyDocumentForm index={index} value={value} />
    ) : null;
}

type PolicyInformationPanelProps = {
    policies: Policy[];
    onAddPolicy: () => void;
    onCancelAddPolicy: (policy: Partial<Policy>) => void;
    onSavePolicy: (policy: Partial<Policy>) => Promise<void>;
    isAddingPolicy?: boolean;
    onChangeTab: (index: number) => void;
    tabIndex: number;
    requirement: Requirement | null;
};

function PolicyInformationPanel({
    policies,
    onAddPolicy,
    onCancelAddPolicy,
    onSavePolicy,
    onChangeTab,
    tabIndex,
    requirement,
}: PolicyInformationPanelProps) {
    const classes = useStyles();

    const a11yProps = (index: number) => ({
        id: `policy-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    });

    return (
        <Flex sx={{flexDirection: 'column', flexGrow: 1}}>
            <Flex sx={{alignItems: 'center', pl: 2, pt: '26px', pr: 5}}>
                <Box sx={{flex: 1}}>
                    <Tabs
                        value={tabIndex}
                        onChange={(_e, i) => onChangeTab(i)}
                        indicatorColor="primary"
                        aria-label="policy tabs"
                    >
                        {policies.map((policy, i) =>
                            policy.status === PolicyStatus.DRAFT ? (
                                <Tab label="New Policy" {...a11yProps(i)} tabIndex={i} key={policy.id} />
                            ) : (
                                <Tab
                                    label={`${policy.carrierName} : ${policy.type}`}
                                    {...a11yProps(i)}
                                    tabIndex={i}
                                    key={policy.id}
                                />
                            ),
                        )}
                    </Tabs>
                </Box>
                <Button type="button" variant="contained" onClick={onAddPolicy} color="primary">
                    <Typography classes={{root: classes.policyAddButtonText}}>+ add new policy</Typography>
                </Button>
            </Flex>
            <ScrollAwareContainer sx={{pl: 2, pr: 5}}>
                {policies.map((policy, i) => (
                    <TabPanel value={tabIndex} index={i} key={policy.id} sx={{py: '26px'}}>
                        <FormPaper sx={{mb: '24px'}}>
                            <PolicyInformationForm
                                index={i}
                                value={policy}
                                onSubmit={onSavePolicy}
                                onCancel={onCancelAddPolicy}
                            />
                        </FormPaper>
                        <Box>
                            <Typography classes={{root: classes.requirementViewerLabel}}>
                                Requirements Viewer
                            </Typography>
                            {requirement && (
                                <FormPaper sx={{mt: '43px'}}>
                                    <PolicyRequirement index={i} value={requirement} />
                                </FormPaper>
                            )}
                        </Box>
                    </TabPanel>
                ))}
            </ScrollAwareContainer>
        </Flex>
    );
}

type PolicyPanelProps = {
    customer?: Customer;
};

function PolicyPanel({customer}: PolicyPanelProps) {
    const firstLoad = useRef(true);
    const history = useHistory();
    const {search, pathname} = useLocation();

    const [envelopeIdToSend, setEnvelopeIdToSend] = useState<string | null>(null);
    const [tabIndex, setTabIndex] = useState(0);
    const [isAddingPolicy, setIsAddingPolicy] = useState(false);
    const [selectedRequirement, setSelectedRequirement] = useState<Requirement | null>(null);

    const {
        data: policies = [],
        isLoading: isLoadingPolicies,
        isFetched,
    } = usePoliciesForCustomerQuery({customerId: customer?.id});

    const {mutateAsync: mutate} = usePolicyMutation();
    const {mutateAsync: mutateRequirement} = useRequirementMutation();

    const currentPolicy = useMemo(() => policies[tabIndex], [policies, tabIndex]);

    useEffect(() => {
        if (firstLoad.current && isFetched) {
            RootStore.uiStore.showSnackbar(`Successfully Loaded Policy Information`, 'success');
            // Using a ref to prevent a re-render
            firstLoad.current = false;
        }
    }, [isFetched]);

    useEffect(() => {
        setSelectedRequirement(null);
    }, [tabIndex]);

    const onSavePolicy = async (policy: Partial<Policy>) => {
        try {
            await mutate({
                ...policy,
                status: PolicyStatus.ACTIVE,
            });

            RootStore.uiStore.showSnackbar(`Successfully Saved Policy Information`, 'success');
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Unknown Error';
            RootStore.uiStore.showSnackbar(`Failed to save policy: ${message}`, 'error');
        }
    };

    const onAddPolicy = async () => {
        if (!customer?.id) return;

        try {
            setIsAddingPolicy(true);
            setTabIndex(tabIndex + 1);
            await mutate({
                customerId: customer?.id,
                label: 'Life Insurance',
                type: '' as PolicyType, // This is just a draft, empty is fine
                status: PolicyStatus.DRAFT,
                number: '',
                carrierName: '',
                coverageAmount: '0.00',
                monthlyContribution: '0.00',
                effectiveDate: format(new Date(), 'yyyy-MM-dd'),
            });
        } catch (e) {
            RootStore.uiStore.showSnackbar(`Failed to add policy.`, 'error');
        } finally {
            setIsAddingPolicy(false);
        }
    };

    const onCancelAddPolicy = async (policy: Partial<Policy>) => {
        setTabIndex(Math.floor(tabIndex - 1));
        await mutate({
            ...policy,
            status: PolicyStatus.DELETED,
        });
    };

    const onAddRequirement = () => {
        if (!customer?.id) return;
        mutateRequirement({
            customerId: customer?.id,
            policyId: currentPolicy?.id,
            type: PolicyRequirementType.POLICY_DOCUSIGN,
            status: PolicyRequirementStatus.OUTSTANDING,
            title: 'Policy Document Requirement',
            private: true,
        });
    };

    const onSelectRequirement = (requirement: Requirement) => {
        setSelectedRequirement(requirement);
    };

    const sendEnvelope = async (envelopeId: string, lockToken?: string | null) => {
        RootStore.uiStore.showSnackbar('Sending document envelope', 'info');
        try {
            const sent = await CustomerPortalAPI.SendDocusignEnvelope(envelopeId, lockToken);
            if (sent) {
                return;
            }
        } catch (error: any) {
            console.log(error);
            RootStore.uiStore.showSnackbar('Could not send document envelope', 'error');
        }
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(search);
        const envelopeId = searchParams.get('envelopeId');
        if (envelopeId) {
            searchParams.delete('envelopeId');
            searchParams.delete('event');
            history.replace({pathname, search: searchParams.toString()});
            // Have to wait for policies to load
            setEnvelopeIdToSend(envelopeId);
        }
    }, []);

    useEffect(() => {
        if (envelopeIdToSend && policies.length) {
            const requirement = policies
                .flatMap((p) => p.requirements)
                .find((r) => r.dataJson.envelopeId === envelopeIdToSend);

            if (!requirement) {
                RootStore.uiStore.showSnackbar('Requirement for document envelope not found', 'error');
            } else {
                sendEnvelope(envelopeIdToSend, requirement.dataJson.docuSignLockToken);
            }

            setEnvelopeIdToSend(null);
        }
    }, [envelopeIdToSend, policies]);

    if (!customer?.id) {
        return <Alert severity="info">Please link a customer</Alert>;
    }

    if (isLoadingPolicies) {
        return <CircularProgress />;
    }

    return (
        <RightSidebarLayout
            main={
                <PolicyInformationPanel
                    policies={policies}
                    tabIndex={tabIndex}
                    onChangeTab={setTabIndex}
                    onAddPolicy={onAddPolicy}
                    onCancelAddPolicy={onCancelAddPolicy}
                    onSavePolicy={onSavePolicy}
                    isAddingPolicy={isAddingPolicy}
                    requirement={selectedRequirement}
                />
            }
            sidebar={
                <PolicyRequirementStatusPanel
                    policy={currentPolicy}
                    selectedRequirement={selectedRequirement}
                    onSelectRequirement={onSelectRequirement}
                    onAddRequirement={onAddRequirement}
                />
            }
        />
    );
}

function PolicyPanelErrorFallback({error, resetErrorBoundary}: FallbackProps) {
    return (
        <Alert severity="error">
            <AlertTitle>Something went wrong:</AlertTitle>
            <pre>{error.message}</pre>
            <Button onClick={resetErrorBoundary} type="button">
                Try again
            </Button>
        </Alert>
    );
}

export default withErrorBoundary(PolicyPanel, {FallbackComponent: PolicyPanelErrorFallback});
